import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { LAgenteDto } from '../dtos/avaliacoes-riscos/l-agente-dto';
import { PessoaDoencaConsultaDto } from '../dtos/doencas/pessoa-doenca-dto';
import { ConsultarPacienteEmContextoEntrada } from '../dtos/exames/consultar-paciente-em-contexto-entrada';
import { LotacaoAtribuicaoDto } from '../dtos/lotacoes-atribuicoes/lotacao-atribuicao-dto';
import { ListarPacientesEntrada } from '../dtos/pacientes/listar-pacientes-entrada';
import { PacienteDto } from '../dtos/pacientes/paciente-dto';
import { PessoaExamePeriodicidadeConsultaDto } from '../dtos/periodicidades/pessoa-exame-periodicidade';
import { UrlService } from './url.service';

@Injectable({
  providedIn: 'root'
})
export class PacienteService {
  private _pacienteAtual: PacienteDto | null = null;

  constructor(private url: UrlService, private http: HttpClient) { 
    this.carregarPacienteDoArmazenamentoLocal();

    this.pacienteAtual$.subscribe((paciente) => {
      this._pacienteAtual = paciente;
    });
  }

  private carregarPacienteDoArmazenamentoLocal() {
    let json = localStorage.getItem('paciente');

    if(json === null)
      return;

    let paciente = JSON.parse(json);

    if(paciente === null)
      return;

    this._pacienteAtual = paciente as PacienteDto;
    this.pacienteAtual$.next(this._pacienteAtual);
  }

  private salvarPacienteNoArmazenamentoLocal() {
    localStorage.setItem('paciente', JSON.stringify(this._pacienteAtual));
  }

  /**
   * Carrega um novo passiente em paciente atual.
   * Este método atualiza o subject pacienteAtual.
   * @param pessoaId Id da pessoa paciente.
   * @returns 
   */
  carregarPacienteEmContexto(entrada: ConsultarPacienteEmContextoEntrada): Observable<PacienteDto> {
    let params = new HttpParams();
    
    if(entrada.atendimentoMedicoId)
      params = params.append('atendimentoMedicoId', entrada.atendimentoMedicoId + '');
    if(entrada.exameBaseId)
      params = params.append('exameBaseId', entrada.exameBaseId + '');
    if(entrada.atestadoMedicoId)
      params = params.append('atestadoMedicoId', entrada.atestadoMedicoId + '');
    if(entrada.contexto)
      params = params.append('contexto', entrada.contexto + '');

    return this.http.get<PacienteDto>(this.url.url('pacientes/em-contexto'), { params: params })
    .pipe(
      tap((paciente) => {
        this._pacienteAtual = paciente;
        this.salvarPacienteNoArmazenamentoLocal();
        this.pacienteAtual$.next(paciente);
      })
    );
  }

  /**
   * Carrega um novo passiente em passiente atual.
   * Este método atualiza o subject pacienteAtual.
   * @param pessoaId Id da pessoa paciente.
   * @returns 
   */
   carregarPaciente(id: number): Observable<PacienteDto> {
    return this.http.get<PacienteDto>(this.url.url('pacientes/' + id))
    .pipe(
      tap((paciente) => {
        this._pacienteAtual = paciente;
        this.salvarPacienteNoArmazenamentoLocal();
        this.pacienteAtual$.next(paciente);
      })
    );
  }

  consultarPacienteEmCache(): Observable<PacienteDto> {
    if(this._pacienteAtual === null)
      throw 'Paciente não carregado.';

    return of(this._pacienteAtual);
  }

  repropagarPaciente(): Observable<PacienteDto | null> {
    this.pacienteAtual$.next(this._pacienteAtual);

    return of(this._pacienteAtual);
  }

  listarPacientes(entrada: ListarPacientesEntrada): Observable<PacienteDto[]> {
    let p = new HttpParams();
    
    let filtro = p
    .append('nomeCpf', entrada.nomeCpf)
    .append('lotacoesFechadas', entrada.lotacoesFechadas);

    return this.http.get<PacienteDto[]>(this.url.url('pacientes'), { params: filtro });
  }

  listarDoencasAtivas(): Observable<PessoaDoencaConsultaDto[]> {
    let id = this._pacienteAtual?.id;

    if(!id)
      throw 'paciente is null';

    return this.http.get<PessoaDoencaConsultaDto[]>(this.url.url(`pacientes/${id}/doencas-ativas`));
  }

  listarDoencasInativas(): Observable<PessoaDoencaConsultaDto[]> {
    let id = this._pacienteAtual?.id;

    if(!id)
      throw 'paciente is null';

    return this.http.get<PessoaDoencaConsultaDto[]>(this.url.url(`pacientes/${id}/doencas-inativas`));
  }

  listarLotacoesAtribuicoes(): Observable<LotacaoAtribuicaoDto[]> {
    let id = this._pacienteAtual?.id;

    if(!id)
      throw 'paciente is null';

    return this.http.get<LotacaoAtribuicaoDto[]>(this.url.url(`pacientes/${id}/lotacoes-atribuicoes`));
  }

  listarAgentes(lotacaoAtribuicaoId: number): Observable<LAgenteDto[]> {
    let id = this._pacienteAtual?.id;

    if(!id)
      throw 'paciente is null';

    return this.http
    .get<LAgenteDto[]>(this.url.url(`pacientes/${id}/agentes?lotacaoId=${lotacaoAtribuicaoId}`));
  }

  listarPeriodicidades(): Observable<PessoaExamePeriodicidadeConsultaDto[]> {
    let id = this._pacienteAtual?.id;

    if(!id)
      throw 'paciente is null';

    return this.http
    .get<PessoaExamePeriodicidadeConsultaDto[]>(this.url.url(`pacientes/${id}/periodicidades`));
  }

  pacienteAtual$: BehaviorSubject<PacienteDto | null> = new BehaviorSubject<PacienteDto | null>(null); 
}
